import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/chat/store'
import { useMemo, useCallback } from 'react'
import { atom, useStore } from 'jotai'
import { useLingeringResolvedIds, useReminderLingerControls } from './reminder-linger'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useIsGatewayConnected } from '@renderer/overlay/atoms'
import { toReminderItems, countOverdueReminders, type ReminderApiItem, type ReminderItemType } from './reminder-mapping'
import { createTempReminderId, insertCreatedReminder, retainOptimisticReminderCreates } from './reminder-create-cache'
import { REMINDERS_QUERY_KEY } from './reminder-query-keys'

export { REMINDERS_QUERY_KEY } from './reminder-query-keys'

// The wire/display shapes and their mapping live in `reminder-mapping` (a leaf
// module, so mapping-only consumers don't pull this hook's dependencies in).
// Re-exported here because most callers reach for them through this module.
export {
  mapApiItemToReminderItem,
  toReminderItem,
  toReminderItems,
  isOverdueReminder,
  countOverdueReminders,
  type ReminderApiItem,
  type ReminderItem,
  type ReminderItemState,
  type ReminderItemType,
  type ReminderSource,
} from './reminder-mapping'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum character length for user-created reminder text. */
export const REMINDER_MAX_LENGTH = 500
/** Ignores a second pin click that lands before the first PATCH settles. */
const PIN_ACTION_LOCK_MS = 400
const lastPinActionAt = new Map<string, number>()

function lockPinAction(id: string): boolean {
  const now = Date.now()
  const last = lastPinActionAt.get(id) ?? 0
  if (now - last < PIN_ACTION_LOCK_MS) {
    return false
  }
  lastPinActionAt.set(id, now)
  return true
}

/**
 * Dev-menu mocks live only in this window's cache — they have no server row, so
 * refetches must keep them and mutations must not call the API for them. Mirrors
 * `isMockAction` for staged actions.
 */
export function isMockReminder(id: string): boolean {
  return id.startsWith('mock-')
}

// ---------------------------------------------------------------------------
// Mutation suppression – prevents gateway events from clobbering optimistic UI
// ---------------------------------------------------------------------------

let lastLocalMutationAt = 0
export function markReminderLocalMutation() {
  lastLocalMutationAt = Date.now()
}
const REMINDER_MUTATION_SUPPRESSION_MS = 2000

const REMINDER_PATCH_MUTATION_KEY = ['reminder-patch']

/** Ticks To-dos' countdown is holding back. Taking one back has nothing on the server to undo. */
const unsentTickIdsAtom = atom<ReadonlySet<string>>(new Set<string>())
export function isRecentReminderLocalMutation() {
  return Date.now() - lastLocalMutationAt < REMINDER_MUTATION_SUPPRESSION_MS
}

// ---------------------------------------------------------------------------
// Fetch helper
// ---------------------------------------------------------------------------

async function fetchWithAuth(url: string, token: string, options?: RequestInit) {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!response.ok) {
    let errorText = 'Request failed'
    try {
      errorText = await response.text()
    } catch {
      // ignore
    }
    throw new Error(errorText || `Request failed with status ${response.status}`)
  }

  return response.json()
}

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/** Manual = user-created in the app; automated = agent/system-authored. */
export function getReminderCreationMethod(
  item: Pick<ReminderApiItem, 'source' | 'jobId' | 'triggeringLlmMessageId'> | null | undefined,
): 'manual' | 'automated' {
  if (!item) return 'manual'
  if (item.source || item.jobId || item.triggeringLlmMessageId) return 'automated'
  return 'manual'
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Analytics context a caller can attach to a reminder mutation — which surface
 * initiated it (HOVER_CLUSTER_TRIGGER / HOVER_CARD_TRIGGER). Omitted for the
 * default in-cell paths so existing dashboards read unchanged.
 */
interface ReminderActionTracking {
  trigger?: string
}

/**
 * Write-only hook for components that only need to modify items without subscribing to state changes.
 * Use this instead of useReminders() to avoid unnecessary re-renders in components like the toolbar.
 */
export function useReminderActions() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const linger = useReminderLingerControls()
  const store = useStore()

  const markTickUnsent = useCallback(
    (id: string, unsent: boolean) =>
      store.set(unsentTickIdsAtom, (prev) => {
        if (prev.has(id) === unsent) return prev
        const next = new Set(prev)
        if (unsent) next.add(id)
        else next.delete(id)
        return next
      }),
    [store],
  )

  // ---- Create mutation -----------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async (input: {
      text: string
      type: ReminderItemType
      dueAt?: string
      attachments?: Array<string>
      priority?: boolean
    }) => {
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute('api/v1/reminders'), token, {
        method: 'POST',
        body: JSON.stringify({
          text: input.text,
          type: input.type,
          ...(input.dueAt && { dueAt: input.dueAt }),
          ...(input.attachments?.length && { attachments: input.attachments }),
          ...(input.priority === true && { priority: true }),
        }),
      })
      return json.data as ReminderApiItem
    },
    onMutate: async (input) => {
      markReminderLocalMutation()
      await queryClient.cancelQueries({ queryKey: [REMINDERS_QUERY_KEY] })
      const previous = queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY])
      const tempId = createTempReminderId()

      const tempItem: ReminderApiItem = {
        id: tempId,
        text: input.text,
        type: input.type,
        dueAt: input.dueAt ?? null,
        source: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        resolvedAt: null,
        attachments: input.attachments ?? [],
        jobId: null,
        metadata: null,
        ...(input.priority === true && { priority: true }),
      }

      queryClient.setQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY], (old) => [tempItem, ...(old ?? [])])
      return { previous, tempId }
    },
    onSuccess: async (created, _input, context) => {
      // The POST response is the row's source of truth, so no refetch follows.
      // A list fetch that started mid-flight would resolve after this write
      // without the new row and clobber it, so cancel it first.
      await queryClient.cancelQueries({ queryKey: [REMINDERS_QUERY_KEY] })
      queryClient.setQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY], (old) =>
        insertCreatedReminder(old, created, context?.tempId),
      )
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData([REMINDERS_QUERY_KEY], context.previous)
      }
    },
  })

  // ---- Patch mutation ------------------------------------------------------

  const patchMutation = useMutation({
    mutationKey: REMINDER_PATCH_MUTATION_KEY,
    // In order, so a tick taken back straight away can't overtake the tick on its way to the server.
    scope: { id: 'reminder-patch' },
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      // Mocks have no server row: the optimistic cache write in onMutate is the
      // whole update, and the refetch merge keeps it.
      if (isMockReminder(id)) {
        return queryClient
          .getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY])
          ?.find((i) => i.id === id) as ReminderApiItem
      }
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute(`api/v1/reminders/${id}`), token, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      return json.data as ReminderApiItem
    },
    onMutate: async ({ id, ...body }) => {
      markReminderLocalMutation()
      await queryClient.cancelQueries({ queryKey: [REMINDERS_QUERY_KEY] })
      const previous = queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY])

      queryClient.setQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY], (old) =>
        (old ?? []).map((item) => {
          if (item.id !== id) return item
          return {
            ...item,
            ...(body.text !== undefined && { text: body.text as string }),
            ...(body.userDescription !== undefined && { userDescription: body.userDescription as string | null }),
            ...(body.dueAt !== undefined && { dueAt: body.dueAt as string | null }),
            ...(body.resolvedAt !== undefined && { resolvedAt: body.resolvedAt as string | null }),
            ...(body.metadata !== undefined && { metadata: body.metadata as Record<string, string> | null }),
            ...(body.attachments !== undefined && { attachments: body.attachments as Array<string> }),
            ...(body.priority !== undefined && { priority: body.priority as boolean }),
            ...(body.manualPriority !== undefined && { manualPriority: body.manualPriority as boolean | null }),
          }
        }),
      )
      // Let go only once the row reads as open. Sooner, it's a resolved row nothing holds, and it drops from the list.
      if (body.resolvedAt === null) linger.cancel(id)

      return { previous }
    },
    onError: (_err, input, context) => {
      if (context?.previous) {
        queryClient.setQueryData([REMINDERS_QUERY_KEY], context.previous)
      }
      if (input.resolvedAt) linger.cancel(input.id)
    },
    onSettled: () => {
      // Refetch after the last patch only. A list fetched sooner would bring back what a later patch is changing.
      if (queryClient.isMutating({ mutationKey: REMINDER_PATCH_MUTATION_KEY }) > 1) {
        return
      }
      queryClient.invalidateQueries({ queryKey: [REMINDERS_QUERY_KEY] })
    },
  })

  // ---- Delete mutation -----------------------------------------------------

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Dropped from the cache in onMutate; the refetch merge won't bring it back.
      if (isMockReminder(id)) return id
      const token = await getAuthToken()
      await fetchWithAuth(getChatApiRoute(`api/v1/reminders/${id}`), token, { method: 'DELETE' })
      return id
    },
    onMutate: async (id) => {
      markReminderLocalMutation()
      await queryClient.cancelQueries({ queryKey: [REMINDERS_QUERY_KEY] })
      const previous = queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY])
      queryClient.setQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY], (old) =>
        (old ?? []).filter((item) => item.id !== id),
      )
      return { previous }
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData([REMINDERS_QUERY_KEY], context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [REMINDERS_QUERY_KEY] })
    },
  })

  // ---- Helpers -------------------------------------------------------------

  const getItemFromCache = useCallback(
    (id: string) => {
      const items = queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY])
      return items?.find((i) => i.id === id)
    },
    [queryClient],
  )

  // ---- Action wrappers -----------------------------------------------------

  const addItem = useCallback(
    (
      title: string,
      type: ReminderItemType = 'note',
      scheduledAt?: Date,
      attachments?: Array<string>,
      meta?: { timeExpressionUsed?: boolean; pinned?: boolean },
    ) => {
      const trimmed = title.trim().slice(0, REMINDER_MAX_LENGTH)
      if (!trimmed && (!attachments || attachments.length === 0)) return
      createMutation.mutate({
        text: trimmed || 'Image note',
        type,
        ...(scheduledAt && { dueAt: scheduledAt.toISOString() }),
        ...(attachments?.length && { attachments }),
        ...(meta?.pinned === true && { priority: true }),
      })
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: 'Create',
        reminderType: type,
        creationMethod: 'manual',
        hasScheduledAt: !!scheduledAt,
        hasAttachments: !!attachments?.length,
        attachmentCount: attachments?.length ?? 0,
        timeExpressionUsed: meta?.timeExpressionUsed,
        priority: meta?.pinned === true,
      })
    },
    [createMutation],
  )

  const dismissItem = useCallback(
    (id: string) => {
      const item = getItemFromCache(id)
      patchMutation.mutate({ id, metadata: { ...item?.metadata, dismissed: 'true' } })
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: 'Dismiss',
        reminderId: id,
        reminderType: item?.type,
        creationMethod: getReminderCreationMethod(item),
      })
    },
    [patchMutation, getItemFromCache],
  )

  const restoreItem = useCallback(
    (id: string) => {
      const item = getItemFromCache(id)
      patchMutation.mutate({ id, metadata: { ...item?.metadata, dismissed: 'false' } })
      AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Restore' })
    },
    [patchMutation, getItemFromCache],
  )

  const updateItem = useCallback(
    (id: string, title: string, scheduledAt?: Date | null, tracking?: ReminderActionTracking) => {
      const trimmed = title.trim().slice(0, REMINDER_MAX_LENGTH)
      if (!trimmed) return
      patchMutation.mutate({
        id,
        text: trimmed,
        ...(scheduledAt !== undefined && { dueAt: scheduledAt ? scheduledAt.toISOString() : null }),
      })
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: 'Edit',
        hasScheduledAt: scheduledAt !== undefined ? !!scheduledAt : undefined,
        ...(tracking?.trigger && { trigger: tracking.trigger }),
      })
    },
    [patchMutation],
  )

  /** Saves the user's notes. Settles with the PATCH, so an autosave can retry a failed one. */
  const setUserDescription = useCallback(
    (id: string, markdown: string) => patchMutation.mutateAsync({ id, userDescription: markdown.trim() || null }),
    [patchMutation],
  )

  const markItemSeen = useCallback(
    (id: string) => {
      const item = getItemFromCache(id)
      if (!item || item.metadata?.dismissed === 'true' || item.metadata?.readAt) return
      patchMutation.mutate({
        id,
        metadata: { ...item.metadata, readAt: new Date().toISOString() },
      })
    },
    [patchMutation, getItemFromCache],
  )

  const setResolved = useCallback(
    (id: string, resolved: boolean, tracking?: ReminderActionTracking) => {
      const item = getItemFromCache(id)
      // Every entry point into completion goes through here — the cell's
      // checkbox, the hover card, the meeting follow-ups list — so this is the
      // one place the linger has to be opened. `patchMutation` closes it.
      if (resolved && linger.start(id)) {
        markTickUnsent(id, true)
      } else if (!resolved && store.get(unsentTickIdsAtom).has(id)) {
        markTickUnsent(id, false)
        linger.cancel(id)
      } else {
        patchMutation.mutate({ id, resolvedAt: resolved ? new Date().toISOString() : null })
      }
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: resolved ? 'Resolve' : 'Reopen',
        reminderId: id,
        reminderType: item?.type,
        creationMethod: getReminderCreationMethod(item),
        ...(tracking?.trigger && { trigger: tracking.trigger }),
      })
    },
    [patchMutation, getItemFromCache, linger, markTickUnsent, store],
  )

  /**
   * Sends the ticks To-dos' countdown held back. Marked resolved before the hold
   * goes, or they'd read as open and come straight back into the list. Any other
   * held tick was sent when it was ticked, and only has its hold to drop.
   */
  const sendHeldTicks = useCallback(
    (ids: Iterable<string>) => {
      const resolvedAt = new Date().toISOString()
      for (const id of ids) {
        if (store.get(unsentTickIdsAtom).has(id) && getItemFromCache(id)) {
          queryClient.setQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY], (old) =>
            (old ?? []).map((other) => (other.id === id ? { ...other, resolvedAt } : other)),
          )
          patchMutation.mutate({ id, resolvedAt })
        }
        markTickUnsent(id, false)
        linger.cancel(id)
      }
    },
    [patchMutation, getItemFromCache, linger, markTickUnsent, queryClient, store],
  )

  /**
   * Pin/unpin. `manualPriority` rides along with the same value so a skill that
   * writes `priority` can't move a reminder the user placed by hand — the
   * server only honours such a write when no user lock is set.
   */
  const setPinned = useCallback(
    (id: string, pinned: boolean, tracking?: ReminderActionTracking) => {
      const item = getItemFromCache(id)
      if (item?.priority === pinned && item.manualPriority === pinned) {
        return
      }
      if (!lockPinAction(id)) {
        return
      }
      patchMutation.mutate({ id, priority: pinned, manualPriority: pinned })
      // Event names predate the rename to pinning; kept so the dashboards that
      // read them stay continuous.
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: pinned ? 'Set Priority' : 'Unset Priority',
        ...(tracking?.trigger && { trigger: tracking.trigger }),
      })
    },
    [patchMutation, getItemFromCache],
  )

  const appendAttachments = useCallback(
    (id: string, fileIds: Array<string>) => {
      if (!fileIds.length) return
      const item = getItemFromCache(id)
      patchMutation.mutate({ id, attachments: [...(item?.attachments ?? []), ...fileIds] })
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: 'Add Attachment',
        attachmentCount: fileIds.length,
        source: 'cell',
      })
    },
    [patchMutation, getItemFromCache],
  )

  const deleteItem = useCallback(
    (id: string, tracking?: ReminderActionTracking) => {
      const item = getItemFromCache(id)
      deleteMutation.mutate(id)
      // Sidepanel delete is the product dismiss path (metadata Dismiss is unused in UI).
      AmplitudeRenderer.trackEvent('Reminder Action', {
        type: 'Dismiss',
        reminderId: id,
        reminderType: item?.type,
        creationMethod: getReminderCreationMethod(item),
        dismissMethod: 'delete',
        ...(tracking?.trigger && { trigger: tracking.trigger }),
      })
    },
    [deleteMutation, getItemFromCache],
  )

  const removeAttachment = useCallback(
    (id: string, fileId: string) => {
      const item = getItemFromCache(id)
      if (!item) return
      patchMutation.mutate({
        id,
        attachments: (item.attachments ?? []).filter((a) => a !== fileId),
      })
      AmplitudeRenderer.trackEvent('Reminder Action', { type: 'Remove Attachment' })
    },
    [patchMutation, getItemFromCache],
  )

  return {
    addItem,
    dismissItem,
    restoreItem,
    updateItem,
    setUserDescription,
    markItemSeen,
    setResolved,
    sendHeldTicks,
    setPinned,
    appendAttachments,
    removeAttachment,
    deleteItem,
  }
}

/**
 * Full hook that subscribes to reminder state and provides actions.
 * Use useReminderActions() instead if you only need to modify items without subscribing to state.
 */
export function useReminders() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const user = useUser()
  const isSignedIn = !!user && !user.isAnonymous
  const isGatewayConnected = useIsGatewayConnected()

  // ---- Query ---------------------------------------------------------------

  const {
    data: apiItems,
    isLoading,
    error,
  } = useQuery({
    queryKey: [REMINDERS_QUERY_KEY],
    queryFn: async (): Promise<Array<ReminderApiItem>> => {
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute('api/v1/reminders'), token)
      const serverItems = (json.data ?? []) as Array<ReminderApiItem>
      const cached = queryClient.getQueryData<Array<ReminderApiItem>>([REMINDERS_QUERY_KEY]) ?? []
      const mocks = cached.filter((item) => isMockReminder(item.id))
      const optimistic = retainOptimisticReminderCreates(cached, serverItems)
      return [...mocks, ...optimistic, ...serverItems]
    },
    enabled: isSignedIn,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: isGatewayConnected ? false : 30 * 1000,
  })

  // ---- Derived data --------------------------------------------------------

  const lingeringResolvedIds = useLingeringResolvedIds()
  // A tick To-dos' countdown is holding hasn't reached the cache, but every list reads it as done.
  const items = useMemo(
    () =>
      toReminderItems(apiItems).map((item) =>
        lingeringResolvedIds.has(item.id) && !item.resolved ? { ...item, resolved: true } : item,
      ),
    [apiItems, lingeringResolvedIds],
  )
  const openItems = useMemo(() => items.filter((i) => i.state !== 'dismissed'), [items])
  const dismissedItems = useMemo(() => items.filter((i) => i.state === 'dismissed'), [items])
  // `openItems` is the View's All tab; dropping the resolved ones gives its
  // Active tab, which is what Home previews — everything still outstanding,
  // pinned or not, and nothing the user has already ticked off.
  const unresolvedItems = useMemo(() => openItems.filter((i) => i.resolved !== true), [openItems])
  // A just-completed reminder holds its slot so it can be un-checked where it
  // was ticked. Counts below stay strict: the badge would be lying if it still
  // counted something the user has struck through.
  const activeItems = useMemo(
    () => openItems.filter((i) => i.resolved !== true || lingeringResolvedIds.has(i.id)),
    [openItems, lingeringResolvedIds],
  )

  const overdueCount = useMemo(() => countOverdueReminders(apiItems), [apiItems])

  // Stable return object so consumers (and the shared RemindersDataProvider)
  // don't re-render on parent-driven re-renders when the underlying data is
  // unchanged. Every field above is already individually memoized.
  return useMemo(
    () => ({
      items,
      openItems,
      dismissedItems,
      activeItems,
      lingeringResolvedIds,
      totalCount: items.length,
      openCount: unresolvedItems.length,
      overdueCount,
      hasHiddenDismissedItems: dismissedItems.length > 0,
      isLoading,
      error,
    }),
    [
      items,
      openItems,
      dismissedItems,
      activeItems,
      lingeringResolvedIds,
      unresolvedItems,
      overdueCount,
      isLoading,
      error,
    ],
  )
}

// TODO: Wire up when backend adds bulk endpoints for reminders
// PATCH /api/v1/reminders (bulk update)
// DELETE /api/v1/reminders (bulk delete)
